import React, { useEffect, useState } from 'react'
import { getProductPurchaseConditionPrice } from '@/checkout-instore-legacy/utils/CatalogUtils'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { getCurrencyCode } from '@/checkout-instore-legacy/utils/PaymentUtils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import ChangePrice from '@/checkout-instore-legacy/components/Commons/ProductManualPrice/ChangePrice'
import ProductManualPrice from '@/checkout-instore-legacy/components/Commons/ProductManualPrice'
import { InstallmentsModal } from './InstallmentsModal'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'
import { InstallmentsRightSideModal } from './InstallmentsRightSideModal'
import { InstallmentsText } from './InstallmentsText'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'
import {
  Installment,
  PlaceHolderPrice,
} from '@/checkout-instore-legacy/product/typings'
import { csx } from '@vtex/admin-ui'
import { PriceTooltip } from './PriceTooltip'
import { ProductPrice } from '@/checkout-instore-legacy/components/ProductPrice/ProductPrice'

const {
  featureToogle: { showDiscountOnProductPage },
} = getInstoreConfig()

export type ProductPagePriceProps = {
  product: Product
  sku: Item | undefined
  allowManualPrice: boolean | undefined
  initialManualPrice: number | null
  placeHolderPrice: PlaceHolderPrice | undefined
  logisticsInfoCache: Record<string, LogisticsInfo>
  isAllVariationsSelected: boolean | undefined
  skuIsAvailableAtInitialQuantity: boolean
  callbackManualPriceChange: (manualPrice: number | null) => void
  installments: Installment[] | undefined
  loadInstallments: (productValue: number) => void
  isLoadingInstallments: boolean
}

export const ProductPagePrice = ({
  product,
  sku,
  allowManualPrice,
  initialManualPrice,
  logisticsInfoCache,
  isAllVariationsSelected,
  skuIsAvailableAtInitialQuantity,
  callbackManualPriceChange,
  installments,
  loadInstallments,
  isLoadingInstallments,
}: ProductPagePriceProps) => {
  const [manualPrice, setManualPrice] = useState(initialManualPrice)
  const [isManualPriceVisible, setIsManualPriceVisible] = useState(false)
  const [showInstallmentsModal, setShowInstallmentsModal] = useState(false)
  const currencyCode = getCurrencyCode() ?? 'BRL'
  const { formatMessage } = useIntl()

  const { enableChangePrice } = getInstoreConfig()

  const originalPriceData = getProductPurchaseConditionPrice(
    product,
    sku,
    isAllVariationsSelected
  )

  const priceData = manualPrice
    ? {
        listPrice: originalPriceData.listPriceInCents,
        price: manualPrice,
      }
    : {
        listPrice: originalPriceData.listPriceInCents,
        price: originalPriceData.priceInCents,
      }

  const { value: isMobile } = useMediaQuery({
    phone: true,
    tablet: true,
    desktop: false,
  })

  const handleClickManualPrice = () => {
    setIsManualPriceVisible(true)
  }

  const handleCloseManualPrice = () => {
    setIsManualPriceVisible(false)
  }

  const handleManualPriceChange = (manualPrice: number | null) => {
    if (manualPrice === priceData.listPrice) {
      manualPrice = null
    }

    setIsManualPriceVisible(false)
    setManualPrice(manualPrice)
    callbackManualPriceChange(manualPrice)
  }

  const handleInstallmentsLabelClick = () => {
    setShowInstallmentsModal(true)
  }

  const handleInstallmentsModalClose = () => {
    setShowInstallmentsModal(false)
  }

  const priceLoadingProps = {
    listPrice: null,
    sellingPrice: null,
  }

  const showStartingAt =
    priceData.price && product.skus.length > 1 && !isAllVariationsSelected

  const productPriceProps = {
    showLabels: false,
    showStartingAt: showStartingAt,
    showSavings: showDiscountOnProductPage,
    showListPrice: isAllVariationsSelected,
    listPrice: priceData.listPrice / 100,
    /**  The sku returned from the sku selector is preferentially available */
    unavailable: !skuIsAvailableAtInitialQuantity,
    sellingPrice: priceData.price / 100,
    currency: currencyCode,
    installments: [],
    ...(isEmpty(logisticsInfoCache) ? priceLoadingProps : {}),
  }

  const Price = () => (
    <div
      className={csx({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      })}
    >
      <ProductPrice {...productPriceProps} />
      <PriceTooltip />
      {allowManualPrice &&
      enableChangePrice &&
      isAllVariationsSelected &&
      skuIsAvailableAtInitialQuantity ? (
        <ChangePrice onClick={handleClickManualPrice} />
      ) : null}
    </div>
  )

  const installmentsModalProps = {
    isOpen: showInstallmentsModal,
    installments: installments ?? [],
    sellingPriceCents: priceData.price,
  }

  useEffect(() => {
    loadInstallments(priceData.price)
  }, [priceData.price, loadInstallments])

  return (
    <div className={csx({ marginTop: '$space-2' })}>
      <div className="dn db-l">
        <Price />
      </div>
      <div className="dn-l">
        <Price />
      </div>
      {isAllVariationsSelected && allowManualPrice && (
        <ProductManualPrice
          name={get(product, 'name')}
          onClose={handleCloseManualPrice}
          onChange={handleManualPriceChange}
          isVisible={isManualPriceVisible}
          originalPrice={priceData.price}
          manualPrice={priceData.listPrice}
        />
      )}
      <InstallmentsText
        onClick={handleInstallmentsLabelClick}
        isLoading={isLoadingInstallments}
        installments={installments}
      />
      {installmentsModalProps.installments.length > 0 &&
        (isMobile ? (
          <InstallmentsModal
            onClose={handleInstallmentsModalClose}
            {...installmentsModalProps}
          />
        ) : (
          <InstallmentsRightSideModal
            title={formatMessage({ id: 'paymentMethod' })}
            setIsOpen={handleInstallmentsModalClose}
            {...installmentsModalProps}
          />
        ))}
    </div>
  )
}

export default ProductPagePrice
